import React, { useEffect, useState } from 'react';

import { Button, Grid, TextField } from '@mui/material';

import Paper from '@mui/material/Paper';
import { SnapshotAlarmSettingForm } from './types';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
} from 'react-hook-form';
import axios from 'axios';

interface Props {
  control: Control<SnapshotAlarmSettingForm>;
  register: UseFormRegister<SnapshotAlarmSettingForm>;
  onSubmit: () => void;
  emailRecipients: string[];
  setEmailRecipients: React.Dispatch<React.SetStateAction<string[]>>;
  handleTestEmail: () => void;
  hasChanges: boolean;
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
  errors: FieldErrors<SnapshotAlarmSettingForm>;
  currentEmail: string;
  setCurrentEmail: React.Dispatch<React.SetStateAction<string>>;
}

const EmailSetup = ({
  control,
  register,
  onSubmit,
  emailRecipients,
  setEmailRecipients,
  handleTestEmail,
  hasChanges,
  setHasChanges,
  errors,
  currentEmail,
  setCurrentEmail,
}: Props) => {
  const [emailError, setEmailError] = useState<string | null>(null);

  // Fetch data from MySQL database using useEffect
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Replace this with your actual API endpoint to fetch data from the MySQL database
        const response = await axios.get(
          '/api/snapshots/alarms/snapshot-alarm-settings'
        );
        const data = response.data;

        // Split the data string into individual emails using the semicolon delimiter
        const emails = data[0].email_recipient
          .split(';')
          .map((email: string) => email.trim());

        // Set the email recipients state with the retrieved emails
        setEmailRecipients(emails);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailRecipientsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const emailValue = event.target.value;
    setCurrentEmail(emailValue);

    if (emailValue.trim() !== '') {
      if (!isValidEmail(emailValue.trim())) {
        setEmailError('Invalid email address');
      } else {
        setEmailError(null);
      }
    } else {
      setEmailError(null);
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ';' || e.key === 'Enter') {
      e.preventDefault(); // Prevent default semicolon behavior (e.g., focus next element)
      if (currentEmail.trim() !== '') {
        if (isValidEmail(currentEmail.trim())) {
          setEmailRecipients([...emailRecipients, currentEmail.trim()]);
          setCurrentEmail(''); // Clear the current email after adding it to recipients
          setHasChanges(true);
        }
      }
    } else if (e.key === 'Backspace' && currentEmail === '') {
      //Handle Backspace key and delete last email recipient
      e.preventDefault(); // Prevent default backspace behavior (e.g., go back in browser history)

      if (emailRecipients.length > 0) {
        setEmailRecipients(emailRecipients.slice(0, -1));
        setHasChanges(true); // Update hasChanges when an email is removed
      }
    }
  };

  const isValidEmail = (email: string) => {
    //Regular Expression for simple email verification
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(email);
  };

  const [isSubmitClicked, setIsSubmitClicked] = useState(false);

  useEffect(() => {
    if (isSubmitClicked) {
      onSubmit();
      setIsSubmitClicked(false); // Reset isSubmitClicked after onSubmit is called
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailRecipients, hasChanges, isSubmitClicked]);

  const handleEmailRemove = (emailToRemove: string) => {
    setEmailRecipients(
      emailRecipients.filter((email) => email !== emailToRemove)
    );
    setHasChanges(true); // Update hasChanges when an email is removed
  };

  return (
    <>
      <Grid container style={{ paddingTop: '1.4rem', paddingRight: '1.4rem' }}>
        <Grid
          container
          item
          justifyContent='center'
          spacing={2}
          xs={8}
          md={6}
          lg={5}
          xl={4}
          component={Paper}
          style={{ margin: 0, paddingRight: '1.1rem', paddingBottom: '1.1rem' }}
        >
          <Controller
            name='email_smtp'
            control={control}
            defaultValue={emailRecipients.join(';')}
            render={({ field: { onChange, value } }) => (
              <Grid item xs={11}>
                <TextField
                  id='email_smtp'
                  label='SMTP Server'
                  name='email_smtp'
                  type='email'
                  value={value || ''}
                  fullWidth
                  onChange={onChange}
                  autoComplete='on'
                />
              </Grid>
            )}
          />
          <Controller
            name='email_port'
            control={control}
            defaultValue={465}
            rules={{ min: 1 }}
            render={({ field: { onChange, value } }) => (
              <Grid item xs={11}>
                <TextField
                  id='email_port'
                  label='SMTP Port'
                  name='email_port'
                  type='number'
                  value={value || ''}
                  fullWidth
                  onChange={onChange}
                  autoComplete='on'
                  inputProps={{ min: 1 }}
                  error={!!errors.email_port}
                />
              </Grid>
            )}
          />
          <Controller
            name='email_username'
            control={control}
            defaultValue={''}
            render={({ field: { onChange, value } }) => (
              <Grid item xs={11}>
                <TextField
                  id='email_username'
                  label='Username'
                  name='email_username'
                  value={value || ''}
                  fullWidth
                  onChange={onChange}
                />
              </Grid>
            )}
          />
          <Controller
            name='email_password'
            control={control}
            defaultValue={''}
            render={({ field: { onChange, value } }) => (
              <Grid item xs={11}>
                <TextField
                  id='email_password'
                  label='Password'
                  type='password'
                  value={value || ''}
                  fullWidth
                  onChange={onChange}
                />
              </Grid>
            )}
          />
          <Controller
            control={control}
            defaultValue={''}
            {...register('email_from', {
              pattern: {
                value:
                  //eslint-disable-next-line
                  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: 'Invalid email address',
              },
            })}
            render={({ field: { onChange, value } }) => (
              <Grid item xs={11}>
                <TextField
                  id='email_from'
                  label='From'
                  name='email_from'
                  type='email'
                  value={value || ''}
                  fullWidth
                  onChange={onChange}
                  autoComplete='on'
                  error={!!errors.email_from}
                  helperText={errors?.email_from?.message}
                />
              </Grid>
            )}
          />
          <Controller
            control={control}
            defaultValue={''}
            {...register('email_recipient')}
            render={({ field: { onChange } }) => {
              const displayEmails = emailRecipients;

              return (
                <Grid item xs={11}>
                  <TextField
                    id='email_recipient'
                    label='Recipient'
                    name='email_recipient'
                    value={currentEmail || ''}
                    fullWidth
                    onChange={(e) => {
                      onChange(e.target.value);
                      handleEmailRecipientsChange(
                        e as React.ChangeEvent<HTMLInputElement>
                      );
                    }}
                    onKeyDown={handleKeyDown}
                    autoComplete='on'
                    error={!!errors.email_recipient || !!emailError}
                    helperText={errors?.email_recipient?.message || emailError}
                  />
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'nowrap',
                      overflowX: 'auto',
                      marginBottom: '10px',
                    }}
                  >
                    {displayEmails.map((email, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'inline-block',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '2px 4px',
                          margin: '2px',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleEmailRemove(email)}
                      >
                        {email}
                      </div>
                    ))}
                  </div>
                </Grid>
              );
            }}
          />
          <Controller
            name='email_subject'
            control={control}
            defaultValue={''}
            render={({ field: { onChange, value } }) => (
              <Grid item xs={11}>
                <TextField
                  id='email_subject'
                  label='Subject'
                  name='email_subject'
                  value={value || ''}
                  fullWidth
                  onChange={onChange}
                  autoComplete='on'
                />
              </Grid>
            )}
          />
          <Controller
            name='email_message'
            control={control}
            defaultValue={''}
            render={({ field: { onChange, value } }) => (
              <Grid item xs={11}>
                <TextField
                  id='email_message'
                  label='Test Message'
                  name='email_message'
                  value={value || ''}
                  fullWidth
                  onChange={onChange}
                  autoComplete='on'
                />
              </Grid>
            )}
          />
          <Grid container item spacing={1} justifyContent='flex-end'>
            <Grid item xs={6} style={{ minWidth: '7rem' }}>
              <Button
                variant='contained'
                color='primary'
                onClick={handleTestEmail}
                fullWidth
              >
                Test Email
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant='contained'
                color='primary'
                onClick={async () => {
                  if (
                    currentEmail.trim() !== '' &&
                    isValidEmail(currentEmail.trim())
                  ) {
                    await setEmailRecipients([
                      ...emailRecipients,
                      currentEmail.trim(),
                    ]);
                    setCurrentEmail('');
                    setHasChanges(true);
                  }
                  setIsSubmitClicked(true); // Set isSubmitClicked to true
                }}
                fullWidth
                disabled={!hasChanges}
              >
                Submit
              </Button>
            </Grid>
            <Grid item xs={12} />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
export default EmailSetup;
